import React, { useRef, useEffect } from "react";
import { FaGripVertical, FaChartBar } from "react-icons/fa";
import { HiCheck, HiPencil } from "react-icons/hi";
import { BackendWidgetRenderer } from "@/features/templates/widgets";
import { Content } from "@/models/document";
import { FiltersContext } from "@/models/filter";
import { BackendWidget } from "@/models/widget-v2";
import ChatDocumentsWidget from "./chat-documents-widget";
import MessageText from "./message-text";
import ToolCallsTimeline from "./tool-calls-timeline";
import QuestionPrompt, { resolveAnswerLabel } from "./question-prompt";
import ActionConfirmCard from "./action-confirm-card";
import {
  ChatMessage,
  ChatTheme,
  ToolCallEntry,
  UserQuestion,
  PendingAction,
} from "./types";

// ============================================================================
// Props
// ============================================================================

interface ChatMessagesProps {
  messages: ChatMessage[];
  theme: ChatTheme;
  filtersCtx: FiltersContext;
  onAddWidgetToDashboard?: (widget: BackendWidget) => void;
  /** Live streaming state */
  isLoading?: boolean;
  isFinishing?: boolean;
  streamingText?: string;
  toolCallsTimeline?: ToolCallEntry[];
  /** Documents found during the current streaming search */
  documentsFromSearch?: Content[];
  /** Pending user question (not yet answered) */
  pendingQuestion?: UserQuestion | null;
  /** Callback when user answers questions (map of question index to selected option IDs) */
  onQuestionAnswer?: (answers: Record<number, string[]>) => void;
  /** Pending action requiring confirmation (ask/plan mode) */
  pendingAction?: PendingAction | null;
  /** Callback when user confirms an action */
  onActionConfirm?: () => void;
  /** Callback when user rejects an action */
  onActionReject?: () => void;
  /** Callback when user wants to edit a previous message (resend from that point) */
  onEditMessage?: (messageId: string, text: string) => void;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Renders the chat messages list with widgets, documents, and tool calls.
 */
export const ChatMessages: React.FC<ChatMessagesProps> = ({
  messages,
  theme,
  filtersCtx,
  onAddWidgetToDashboard,
  isLoading = false,
  isFinishing = false,
  streamingText = "",
  toolCallsTimeline = [],
  documentsFromSearch = [],
  pendingQuestion = null,
  onQuestionAnswer,
  pendingAction = null,
  onActionConfirm,
  onActionReject,
  onEditMessage,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingText, toolCallsTimeline]);

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[52rem] px-6 pt-10 pb-6">
        <div className="flex flex-col gap-8">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`
                flex gap-3
                ${message.sender === "user" ? "justify-end" : "justify-start"}
                animate-[fadeSlideIn_0.25s_ease-out]
              `}
            >
              {message.sender !== "user" && (
                <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-accent/10 mt-0.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-accent" />
                </div>
              )}

              <div
                className={`flex flex-col gap-2 ${message.sender === "user" ? "items-end max-w-[75%]" : "flex-1 min-w-0"}`}
              >
                {message.toolCalls && message.toolCalls.length > 0 && (
                  <div
                    className={`
                      w-full rounded-2xl px-4 py-3
                      bg-white/[0.03] ${theme.border} border
                    `}
                  >
                    <ToolCallsTimeline
                      toolCalls={message.toolCalls}
                      isLive={false}
                      theme={theme}
                    />
                  </div>
                )}

                {message.text && message.sender === "user" && (
                  <div className="group/msg relative">
                    <div
                      className={`rounded-2xl bg-white/[0.08] px-5 py-3 ${theme.textPrimary}`}
                    >
                      <MessageText
                        text={message.text}
                        className="text-[15px] leading-[1.7]"
                      />
                    </div>
                    {onEditMessage &&
                      !isLoading &&
                      !message.userQuestionResponse && (
                        <button
                          onClick={() =>
                            onEditMessage(message.id, message.text)
                          }
                          className={`
                          absolute -bottom-2 right-2 p-1.5 rounded-lg
                          opacity-0 group-hover/msg:opacity-100
                          bg-white/10 hover:bg-white/20 ${theme.textMuted} hover:text-white
                          transition-all duration-150
                        `}
                          aria-label="Modifier"
                        >
                          <HiPencil className="h-3 w-3" />
                        </button>
                      )}
                  </div>
                )}

                {message.text && message.sender !== "user" && (
                  <div
                    className={`rounded-2xl bg-white/[0.03] px-5 py-3.5 ${theme.assistantMsgText}`}
                  >
                    <MessageText
                      text={message.text}
                      className="text-[15px] leading-[1.7]"
                    />
                  </div>
                )}

                {message.widget && (
                  <div
                    className={`
                      w-full overflow-hidden rounded-2xl
                      bg-white/[0.03] ${theme.border} border
                    `}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData(
                        "application/x-widget",
                        JSON.stringify(message.widget),
                      );
                      e.dataTransfer.effectAllowed = "copy";
                    }}
                  >
                    <div
                      className={`
                        flex items-center justify-between px-4 py-2.5
                        ${theme.border} border-b
                      `}
                    >
                      <div
                        className={`flex items-center gap-2 text-xs ${theme.textMuted}`}
                      >
                        <FaGripVertical className="cursor-grab" />
                        <span>Glissez vers le dashboard</span>
                      </div>
                      {onAddWidgetToDashboard && (
                        <button
                          onClick={() =>
                            onAddWidgetToDashboard(message.widget!)
                          }
                          className="flex items-center gap-1.5 rounded-lg bg-accent px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-accent-dark"
                        >
                          <FaChartBar className="h-3 w-3" />
                          Ajouter
                        </button>
                      )}
                    </div>
                    <div className="p-3" style={{ height: "300px" }}>
                      <BackendWidgetRenderer
                        widget={message.widget}
                        filtersCtx={filtersCtx}
                      />
                    </div>
                  </div>
                )}

                {message.documents && message.documents.length > 0 && (
                  <ChatDocumentsWidget
                    documents={message.documents}
                    theme={theme}
                  />
                )}

                {message.userQuestion && message.userQuestionResponse && (
                  <div
                    className={`
                      w-full rounded-2xl px-4 py-3
                      bg-white/[0.03] ${theme.border} border
                    `}
                  >
                    <p className={`text-xs ${theme.textMuted} mb-2`}>
                      {message.userQuestion.title || "Réponse"}
                    </p>
                    {message.userQuestion.questions.map((q, qIdx) => {
                      const matchedResponses =
                        message.userQuestionResponse!.filter(
                          (id) =>
                            id.startsWith("__custom__:") ||
                            q.options.some((o) => o.id === id),
                        );
                      if (matchedResponses.length === 0) return null;
                      return (
                        <div key={qIdx} className={qIdx > 0 ? "mt-3" : ""}>
                          <p className={`text-sm ${theme.textSecondary} mb-2`}>
                            {q.question}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {matchedResponses.map((selectedId) => (
                              <span
                                key={selectedId}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent text-white text-xs font-medium"
                              >
                                <HiCheck className="h-3 w-3" />
                                {resolveAnswerLabel(selectedId, q.options)}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Loading state */}
          {isLoading && !isFinishing && (
            <div className="flex gap-3 animate-[fadeSlideIn_0.25s_ease-out]">
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-accent/10 mt-0.5">
                <div className="h-2.5 w-2.5 rounded-full bg-accent animate-pulse" />
              </div>
              <div className="flex-1 min-w-0 flex flex-col gap-2">
                {toolCallsTimeline.length > 0 && (
                  <div
                    className={`
                      w-full rounded-2xl px-4 py-3
                      bg-white/[0.03] ${theme.border} border
                    `}
                  >
                    <ToolCallsTimeline
                      toolCalls={toolCallsTimeline}
                      isLive={true}
                      theme={theme}
                    />
                    {documentsFromSearch.length > 0 && (
                      <div className={`mt-2 pt-2 border-t ${theme.border}`}>
                        <ChatDocumentsWidget
                          documents={documentsFromSearch}
                          theme={theme}
                          initialLimit={2}
                        />
                      </div>
                    )}
                  </div>
                )}

                <div
                  className={`rounded-2xl bg-white/[0.03] px-5 py-3.5 ${theme.assistantMsgText}`}
                >
                  {streamingText ? (
                    <div>
                      <MessageText
                        text={streamingText}
                        className="text-[15px] leading-[1.7]"
                      />
                      <div className="mt-3 flex items-center gap-1.5">
                        <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />
                        <span className={`text-xs ${theme.textMuted}`}>
                          En cours...
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2.5 py-1">
                      <div className="flex gap-1">
                        <div
                          className="h-1.5 w-1.5 animate-[bounce_1s_infinite] rounded-full bg-accent/70"
                          style={{ animationDelay: "0ms" }}
                        />
                        <div
                          className="h-1.5 w-1.5 animate-[bounce_1s_infinite] rounded-full bg-accent/70"
                          style={{ animationDelay: "150ms" }}
                        />
                        <div
                          className="h-1.5 w-1.5 animate-[bounce_1s_infinite] rounded-full bg-accent/70"
                          style={{ animationDelay: "300ms" }}
                        />
                      </div>
                      <span className={`text-xs ${theme.textMuted}`}>
                        {toolCallsTimeline.length > 0 &&
                        toolCallsTimeline.some((tc) => tc.status === "pending")
                          ? "Exécution..."
                          : "Réflexion..."}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Pending user question */}
          {pendingQuestion && onQuestionAnswer && !isLoading && (
            <div className="flex gap-3 animate-[fadeSlideIn_0.25s_ease-out]">
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-accent/10 mt-0.5">
                <div className="h-2.5 w-2.5 rounded-full bg-accent" />
              </div>
              <div className="flex-1 min-w-0">
                <QuestionPrompt
                  question={pendingQuestion}
                  theme={theme}
                  onAnswer={onQuestionAnswer}
                />
              </div>
            </div>
          )}

          {/* Pending action confirmation */}
          {pendingAction && onActionConfirm && onActionReject && !isLoading && (
            <div className="flex gap-3 animate-[fadeSlideIn_0.25s_ease-out]">
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-accent/10 mt-0.5">
                <div className="h-2.5 w-2.5 rounded-full bg-accent" />
              </div>
              <div className="flex-1 min-w-0">
                <ActionConfirmCard
                  action={pendingAction}
                  theme={theme}
                  onConfirm={onActionConfirm}
                  onReject={onActionReject}
                  disabled={pendingAction.status === "executing"}
                />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );
};

ChatMessages.displayName = "ChatMessages";

export default ChatMessages;
